import { useState } from 'react';
import { Plus } from 'lucide-react';
import { Filter, FilterState, FilterableField } from './types';
import { FilterChip } from './FilterChip';
import { AttributeSelector } from './AttributeSelector';
import { LogicToggle } from './LogicToggle';

interface FilterBuilderProps {
  fields: FilterableField[];
  value: FilterState;
  onChange: (state: FilterState) => void;
}

export function FilterBuilder({ fields, value, onChange }: FilterBuilderProps) {
  const [showAttributeSelector, setShowAttributeSelector] = useState(false);

  const handleAddFilter = (field: FilterableField) => {
    const newFilter: Filter = {
      id: `filter-${Date.now()}`,
      field: field.key,
      operator: '',
      value: null,
    };
    onChange({
      ...value,
      filters: [...value.filters, newFilter],
    });
    setShowAttributeSelector(false);
  };

  const handleUpdateFilter = (filterId: string, updatedFilter: Filter) => {
    onChange({
      ...value,
      filters: value.filters.map((f) => (f.id === filterId ? updatedFilter : f)),
    });
  };

  const handleRemoveFilter = (filterId: string) => {
    onChange({
      ...value,
      filters: value.filters.filter((f) => f.id !== filterId),
    });
  };

  const handleLogicChange = (logic: 'and' | 'or') => {
    onChange({ ...value, logic });
  };

  const getFieldConfig = (fieldKey: string): FilterableField | undefined => {
    return fields.find((f) => f.key === fieldKey);
  };

  return (
    <div className="flex items-center gap-2 flex-wrap">
      <div className="relative">
        <button
          type="button"
          onClick={() => setShowAttributeSelector(!showAttributeSelector)}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-orange-600 hover:bg-orange-50 rounded-lg transition-colors cursor-pointer"
        >
          <Plus className="w-4 h-4" />
          Adicionar filtro
        </button>

        {showAttributeSelector && (
          <AttributeSelector
            fields={fields}
            onSelect={handleAddFilter}
            onClose={() => setShowAttributeSelector(false)}
          />
        )}
      </div>

      {value.filters.map((filter, index) => {
        const fieldConfig = getFieldConfig(filter.field);
        if (!fieldConfig) return null;

        return (
          <div key={filter.id} className="inline-flex items-center">
            {index > 0 && (
              <LogicToggle value={value.logic} onChange={handleLogicChange} />
            )}
            <FilterChip
              filter={filter}
              field={fieldConfig}
              onUpdate={(updated) => handleUpdateFilter(filter.id, updated)}
              onRemove={() => handleRemoveFilter(filter.id)}
            />
          </div>
        );
      })}
    </div>
  );
}
